/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import clsx from 'clsx';
import React, { FC, useContext, useRef } from 'react';
import { WindowScroller, WindowScrollerChildProps } from 'react-virtualized';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { PredictionsAccountCategories, PredictionsSubCategories, VIEW_TYPES } from '../../../core/types';
import { DataGrid } from '../../../ui/data-grid/components/data-grid';
import { DataGridLayoutProvider } from '../../../ui/data-grid/components/data-grid-layout-context';
import { Rows } from '../reports/data-grid/rows';
import { SIZES } from '../../../core/constants';
import { HeaderRows } from '../reports/data-grid/header-rows';
import { ViewCollapseExpand } from '../../../ui/view-menu/view-collapse-expand';
import { HeaderRowsVirtualized } from '../reports/data-grid/header-rows-virtualized';
import { RowsVirtualized } from '../reports/data-grid/rows-virtualized';
import { getAccountRowsWithFYInfo, getTotalAccountRowsWithFYInfo } from '../reports/data-grid/format-rows';
import { getColors } from '../reports/data-grid/style-rows';
import { StickyHeaders } from './components/sticky-headers';
import { adjustForPastDates, getColumnsForPredictionOrCashImpact } from './utils/forecast-columns';
import styles from './predictions.scss';
import useDraggableResize from '../../../hooks/useDraggableResize';
import { ForecastingTitleHeader } from './forecasting-title-header';
import { ForecastingNavigationTabs } from './navigation/forecasting-navigation-tabs';
import { DIRECTION, PeriodTableControl } from '../components/period-menu/period-table-control';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { OverlayExplanationsContainer } from './overlay-explanations-container';
import { usePredictionsInfoContext } from '../context/predictions-info-context';
import { RowsType, TotalRow } from '../reports/data-grid/types';
import { RowHeights } from '../reports/user-grid-settings/types';
import { TABLE_REF_INDICES, usePredictionsDataGrid } from './hooks';

interface Props {
  rowHeights: RowHeights;
  accountRows?: RowsType | null;
  totalRow: TotalRow | null;
  totalCashPredictRow?: TotalRow | null;
  category: PredictionsAccountCategories;
  viewOptions?: { name: string; value: VIEW_TYPES }[];
  categoryOptions?: { name: string; value: PredictionsSubCategories }[];
  reportColumns?: string[];
  showDaysToPay?: boolean;
}

export const ProfitLossPredictionsPageWrapper: FC<Props> = ({
  reportColumns,
  accountRows,
  totalRow,
  totalCashPredictRow,
  category,
  viewOptions,
  categoryOptions,
  rowHeights,
  showDaysToPay
}: Props) => {
  const intl = useIntl();
  const listContainerRef = useRef<HTMLDivElement>(null);

  const {
    isFetchingReport,
    periodType,
    totalPastDates,
    fetchForecastReport,
    viewType,
    forecast,
    rowHeaderWidth,
    setRowHeaderWidth,
    showPredictionsExplanation
  } = usePredictionsInfoContext();

  const { financialConfig } = useContext(PredictManagementContext);

  const isPaymentsView = viewType === VIEW_TYPES.CASH_FLOW;

  const { onMouseDown: onScrollContainerMouseDown } = useDraggableResize(listContainerRef, {
    minX: 200,
    direction: 'horizontal',
    onUpdate: setRowHeaderWidth
  });

  const { updateScrollLeft, handlePeriodTableControl, tables, headlineHeaderRef } = usePredictionsDataGrid({
    viewOptions,
    categoryOptions
  });

  if (!periodType) {
    return null;
  }

  const columns = getColumnsForPredictionOrCashImpact(
    reportColumns || [],
    isPaymentsView,
    periodType,
    financialConfig
  );

  let totalRows = totalRow ? [totalRow] : [];
  if (viewType !== VIEW_TYPES.CASH_FLOW && periodType === 'MONTHLY') {
    accountRows = getAccountRowsWithFYInfo(accountRows || [], columns, category);
    if (totalRow) {
      totalRows = getTotalAccountRowsWithFYInfo([totalRow], columns, category);
    }
  }

  const hideBudgetData = forecast?.kind === 'budget';
  const hideCashGraph = viewType !== VIEW_TYPES.CASH_FLOW;

  return (
    <DataGridLayoutProvider
      columnCount={columns.length}
      onScrollPositionChange={updateScrollLeft}
    >
      <section>
        <>
          <ForecastingTitleHeader />
          <ForecastingNavigationTabs />
        </>

        {!!reportColumns?.length && (!!accountRows?.length || !!totalRows.length) && (
          <>
            {hideBudgetData ? null : (
              <div
                className={clsx(
                  styles.isSticky,
                  styles.cashFlowContainerNewForecasting,
                  styles.isStickyNewForecastingDesignsTop
                )}
              >
                <section data-testid="predicted-cash-grid">
                  <StickyHeaders columns={columns} headerRef={headlineHeaderRef} />
                  {hideCashGraph ? (
                    <PeriodTableControl
                      onBackClick={handlePeriodTableControl(DIRECTION.back)}
                      onForwardClick={handlePeriodTableControl(DIRECTION.forward)}
                      hideCashGraph
                    />
                  ) : totalCashPredictRow ? (
                    <DataGrid
                      totalPastDates={totalPastDates}
                      disablePastScrolling={isPaymentsView}
                      isLoading={isFetchingReport}
                      headers={<HeaderRows totalRows={[totalCashPredictRow]} rowSize={SIZES.LARGE} />}
                      rows={
                        <Rows
                          totalRows={isPaymentsView ? [adjustForPastDates(totalCashPredictRow)] : [totalCashPredictRow]}
                          rowHeightOverride={SIZES.LARGE}
                          columns={columns}
                          chartCustomSettings={{ yesterday: !isPaymentsView }}
                        />
                      }
                      tableForwardRef={tables[TABLE_REF_INDICES.PREDICTED_CASH]}
                      onScroll={updateScrollLeft}
                      periodType={periodType}
                      isMainHeadlineGrid={true}
                    />
                  ) : null}
                </section>
              </div>
            )}

            {hideBudgetData && (
              <div
                className={clsx(
                  styles.isSticky,
                  styles.cashFlowContainerNewForecasting,
                  styles.isStickyNewForecastingDesignsTop
                )}
              >
                <section data-testid="predicted-cash-grid">
                  <StickyHeaders columns={columns} headerRef={headlineHeaderRef} />
                  {totalCashPredictRow ? (
                    <DataGrid
                      totalPastDates={totalPastDates}
                      disablePastScrolling={isPaymentsView}
                      isLoading={isFetchingReport}
                      headers={<HeaderRows totalRows={[totalCashPredictRow]} rowSize={SIZES.LARGE} />}
                      rows={
                        <Rows
                          totalRows={isPaymentsView ? [adjustForPastDates(totalCashPredictRow)] : [totalCashPredictRow]}
                          rowHeightOverride={SIZES.LARGE}
                          columns={columns}
                          chartCustomSettings={{ yesterday: !isPaymentsView }}
                          hideBudgetData={true}
                        />
                      }
                      tableForwardRef={tables[TABLE_REF_INDICES.PREDICTED_CASH]}
                      onScroll={updateScrollLeft}
                      periodType={periodType}
                      isMainHeadlineGrid={true}
                      hideBudgetData={true}
                    />
                  ) : null}
                </section>
              </div>
            )}

            <div className={styles.top}>
              {!!totalRows.length && (
                <DataGrid
                  disablePastScrolling={isPaymentsView}
                  isLoading={isFetchingReport}
                  headers={
                    <HeaderRows totalRows={totalRows} rowSize={SIZES.MEDIUM} categoryOptions={categoryOptions} />
                  }
                  rows={
                    <Rows
                      totalRows={isPaymentsView ? [adjustForPastDates(totalRows[0])] : totalRows}
                      rowHeightOverride={SIZES.MEDIUM}
                      columns={columns}
                      category={!isPaymentsView ? category : undefined}
                      chartCustomSettings={{
                        colors: getColors(category, viewType),
                        thinLines: true,
                        yesterday: false
                      }}
                    />
                  }
                  tableForwardRef={tables[TABLE_REF_INDICES.HEADLINES]}
                  onScroll={updateScrollLeft}
                  periodType={periodType}
                  showTableControls={false}
                  isMainHeadlineGrid={true}
                />
              )}

              {!!accountRows?.length && (
                <div
                  className={clsx(styles.labelContainer, styles.labelContainerNewForecasting)}
                  style={{ width: rowHeaderWidth }}
                >
                  <ViewCollapseExpand
                    customExpandName={intl.formatMessage({
                      id: 'predict.menu.expand_accounts',
                      defaultMessage: 'Expand Accounts'
                    })}
                    customCollapseName={intl.formatMessage({
                      id: 'predict.menu.collapse_accounts',
                      defaultMessage: 'Collapse Accounts'
                    })}
                  />
                  {showDaysToPay && (
                    <div className={styles.gridLabel}>
                      <FormattedMessage id="predict.predictions.days_to_pay" defaultMessage="days to pay" />
                    </div>
                  )}
                </div>
              )}

              <div
                data-testid="category-rows-grid"
                className={styles.categoryRowsGrid}
                style={{ position: 'relative' }}
              >
                <WindowScroller scrollElement={window}>
                  {(windowScrollerChildProps: WindowScrollerChildProps) => {
                    const { registerChild } = windowScrollerChildProps as WindowScrollerChildProps & {
                      registerChild?: (element: Element | null) => void;
                    };

                    return (
                      <div ref={registerChild}>
                        <DataGrid
                          // props to trigger initial scroll position updates
                          // this should only be applied to a single datagrid
                          disablePastScrolling={isPaymentsView}
                          totalPastDates={totalPastDates}
                          onScrollEnd={fetchForecastReport}
                          isLoading={isFetchingReport}
                          headers={
                            accountRows ? (
                              <HeaderRowsVirtualized
                                rows={accountRows}
                                windowScrollerChildProps={windowScrollerChildProps}
                                isPayments={isPaymentsView}
                                category={category}
                                viewType={viewType as VIEW_TYPES}
                                rowHeaderWidth={rowHeaderWidth}
                                onListContainerMouseDown={onScrollContainerMouseDown}
                                listContainerRef={listContainerRef}
                                isBudget={forecast?.kind === 'budget'}
                              />
                            ) : undefined
                          }
                          rows={
                            accountRows ? (
                              <RowsVirtualized
                                rows={accountRows}
                                windowScrollerChildProps={windowScrollerChildProps}
                                columns={columns}
                                category={category}
                                viewType={viewType as VIEW_TYPES}
                              />
                            ) : undefined
                          }
                          tableForwardRef={tables[TABLE_REF_INDICES.CONTENT]}
                          onScroll={updateScrollLeft}
                          periodType={periodType}
                          showTableControls={false}
                        />
                      </div>
                    );
                  }}
                </WindowScroller>
                {accountRows && (
                  <OverlayExplanationsContainer
                    rows={accountRows}
                    rowHeights={rowHeights}
                    rowHeaderWidth={rowHeaderWidth}
                    showPredictionsExplanation={showPredictionsExplanation}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </DataGridLayoutProvider>
  );
};
